import { type OverlayHitTarget } from './type';

/** 기록된 판정 대상과 그린 순번. 순번이 클수록 나중에(화면에서 더 앞에) 그린 대상입니다. */
export type OverlayHitEntry = {
  order: number;
  target: OverlayHitTarget;
};

export type OverlayHitBounds = {
  maxX: number;
  maxY: number;
  minX: number;
  minY: number;
};

type Slot = {
  bounds: Nullish<OverlayHitBounds>;
  entryList: OverlayHitEntry[];
};

const createSlot = (): Slot => ({ bounds: null, entryList: [] });

const contains = (target: OverlayHitTarget, x: number, y: number) => {
  if (target.shape === 'rect') {
    return (
      x >= target.x &&
      x <= target.x + target.width &&
      y >= target.y &&
      y <= target.y + target.height
    );
  }
  const dx = x - target.x;
  const dy = y - target.y;
  return dx * dx + dy * dy <= target.radius * target.radius;
};

const extendBounds = (slot: Slot, target: OverlayHitTarget) => {
  const minX = target.shape === 'rect' ? target.x : target.x - target.radius;
  const minY = target.shape === 'rect' ? target.y : target.y - target.radius;
  const maxX =
    target.shape === 'rect'
      ? target.x + target.width
      : target.x + target.radius;
  const maxY =
    target.shape === 'rect'
      ? target.y + target.height
      : target.y + target.radius;
  const { bounds } = slot;
  if (!bounds) {
    slot.bounds = { maxX, maxY, minX, minY };
    return;
  }
  bounds.minX = Math.min(bounds.minX, minX);
  bounds.minY = Math.min(bounds.minY, minY);
  bounds.maxX = Math.max(bounds.maxX, maxX);
  bounds.maxY = Math.max(bounds.maxY, maxY);
};

/**
 * 엔진 인스턴스별 판정 데이터. 레이어(칸)마다 이번 도장에서 실제로 그린 사각형·원을 보관합니다.
 *
 * 두 가지 쓰기 경로가 있습니다.
 * - 도장 중: `beginPaint` ~ `commitPaint` 사이의 쓰기는 임시 칸에 쌓이고, 도장이 성공하면
 *   한꺼번에 확정됩니다. 실패하거나 카메라가 없으면 확정하지 않고 비웁니다.
 * - 도장 밖: 바로 확정 데이터에 씁니다(측정 래퍼와 단위 테스트용).
 * 순번은 칸·레이어를 넘어 하나로 증가하므로 같은 priority 의 서로 다른 레이어 대상도
 * "실제로 나중에 그린 쪽"이 앞에 옵니다.
 */
export const createOverlayHitRegistry = () => {
  let committed = new Map<string, Slot>();
  let staged: Nullish<Map<string, Slot>> = null;
  let sequence = 0;

  const getWriteSlot = (layerId: string) => {
    const map = staged ?? committed;
    let slot = map.get(layerId);
    if (!slot) {
      slot = createSlot();
      map.set(layerId, slot);
    }
    return slot;
  };

  return {
    /** 대상 하나를 그린 순서대로 기록합니다. */
    add: (layerId: string, target: OverlayHitTarget) => {
      sequence += 1;
      const slot = getWriteSlot(layerId);
      slot.entryList.push({ order: sequence, target });
      extendBounds(slot, target);
    },
    /** 도장 시작. 이번 도장의 대상 데이터를 새로 쌓기 시작합니다. */
    beginPaint: () => {
      staged = new Map();
    },
    /** 모든 칸을 비웁니다(카메라 없음·뷰포트 0·캔버스 교체·도장 실패). */
    clearAll: () => {
      committed = new Map();
      staged = null;
    },
    /** 한 칸을 비웁니다. 도장 중이면 이번 도장의 칸도 함께 비웁니다. */
    clearLayer: (layerId: string) => {
      committed.delete(layerId);
      staged?.delete(layerId);
    },
    /** 도장 성공. 이번에 쌓은 대상 데이터를 확정합니다. */
    commitPaint: () => {
      if (!staged) return;
      committed = staged;
      staged = null;
    },
    /** 좌표에 걸리는 가장 나중에 그린 대상을 찾습니다. `accept` 가 거짓인 대상은 건너뜁니다. */
    findTop: (
      layerId: string,
      x: number,
      y: number,
      accept?: (payload: unknown) => boolean,
    ): Nullish<OverlayHitEntry> => {
      const slot = committed.get(layerId);
      if (!slot) return null;
      for (let i = slot.entryList.length - 1; i >= 0; i -= 1) {
        const entry = slot.entryList[i];
        if (!contains(entry.target, x, y)) continue;
        if (accept && !accept(entry.target.payload)) continue;
        return entry;
      }
      return null;
    },
    /** 칸의 경계 상자. 기록이 없으면 null. */
    getBounds: (layerId: string): Nullish<OverlayHitBounds> =>
      committed.get(layerId)?.bounds ?? null,
    /** 모든 칸과 순번을 초기화합니다(테스트·엔진 폐기용). */
    reset: () => {
      committed = new Map();
      staged = null;
      sequence = 0;
    },
  };
};

export type OverlayHitRegistry = ReturnType<typeof createOverlayHitRegistry>;
